/**
 * Custom HTML element that provides class-of-trade filter functionality.
 * Displays filter buttons for different trade categories (store, restaurant, entertainment, bar, other)
 * and emits events when selections change.
 *
 * @example
 * ```html
 * <awe-cot-filters allowed-trades="store,restaurant,bar"></awe-cot-filters>
 * ```
 *
 * @fires AweClassOfTradeFilters#avails-facet-change - Fired when filter selection changes
 */
class AweClassOfTradeFilters extends HTMLElement {
    /**
     * Creates an instance of AweClassOfTradeFilters.
     * Initializes the shadow DOM, creates the container element, sets up icon mappings,
     * creates the clear button, and applies component styles.
     */
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.container = document.createElement('div');
        this.container.classList.add('cot-ctnr');
        // Define all possible items
        this.allowedTrades = null; // Will be set if attribute is passed
        this.selectedItems = new Set();
        // map class-of-trade to icon name
        this.iconMapping = {
            store: 'icon-basket',
            restaurant: 'icon-restaurant',
            entertainment: 'icon-ticket',
            bar: 'icon-glass',
            other: 'icon-asterisk',
        };
        // Clear button
        const btn = document.createElement('button');
        btn.classList.add('clear-button');
        btn.innerHTML = '<i class=\'icon-cancel\'></i>';
        btn.disabled = true;
        btn.addEventListener('click', this.clearFilters.bind(this));
        btn.setAttribute('aria-label', window.i18n.get('AWE-COT-FILTERS.CLEAR'));
        this.clearButton = btn;
        // Add main styles
        const style = document.createElement('style');
        style.textContent = `
      @import url('../assets/d516247/fonts/fontello.css');

      .cot-ctnr {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
      }
      .cot-filter {
        display: flex;
        align-items: center;
      }
      input {
        display: none;
      }

      label, button {
        color: var(--btn-toggle-color, #000);
        background-color: var(--cot-bg-color, #fff);
        border: 1px solid var(--cot-border-color, var(--border-color));
        border-radius: var(--border-radius, 0);
        padding: 5px 10px;
        cursor: pointer;
      }

      .cot-filter input:checked + label {
        background-color: var(--btn-toggle-color);
        color: #fff;
      }

      button:disabled {
        opacity: 0.1;
        cursor: not-allowed;
      }
    `;
        this.shadowRoot?.append(style, this.container);
    }
    /**
     * Specifies which attributes should be observed for changes.
     * When these attributes change, attributeChangedCallback will be called.
     *
     * @returns {string[]} Array containing the attribute names to observe
     */
    static get observedAttributes() {
        return ['allowed-trades', 'bar-icon'];
    }
    /**
     * Called when one of the observed attributes changes.
     * Currently handles the 'allowed-trades' attribute by parsing the comma-separated
     * list of trade types and updating the filter display.
     *
     * @param {string} name - The name of the attribute that changed
     * @param {string} _ - The old value (unused)
     * @param {string} newValue - The new value of the attribute
     */
    attributeChangedCallback(name, _, newValue) {
        try {
            switch (name) {
                case 'allowed-trades':
                    this.allowedTrades = newValue.split(',');
                    break;
                case 'bar-icon':
                    this.iconMapping.bar = `icon-${newValue || 'glass'}`;
                    break;
            }
            this.updateFilters();
        }
        catch (err) {
            console.error('Error updating COT filters:', err); // eslint-disable-line no-console
        }
    }
    /**
     * Called when the element is inserted into the DOM.
     * Ensures the component is properly connected before updating filters.
     */
    connectedCallback() {
        if (!this.isConnected)
            return;
        this.updateFilters();
    }
    /**
     * Builds and updates the list of filter buttons based on allowed trades.
     * Creates DOM elements for each trade type with checkboxes, labels, and icons.
     * Also sets up event handlers for keyboard navigation and selection changes.
     *
     * @private
     */
    updateFilters() {
        const elements = []; // list of DOM elements to add in container
        this.allowedTrades?.forEach((trade) => {
            const div = document.createElement('div');
            div.classList.add('cot-filter');
            const input = document.createElement('input');
            input.type = 'checkbox';
            input.id = `cot-${trade}`;
            input.value = trade;
            const label = document.createElement('label');
            label.setAttribute('for', input.id);
            label.setAttribute('tabindex', '0');
            label.setAttribute('aria-label', window.i18n.get(`AWE-COT-FILTERS.${trade.toUpperCase()}`));
            label.innerHTML = `<i class='${this.iconMapping[trade]}'></i>`;
            // Allow keyboard navigation
            label.addEventListener('keypress', (e) => {
                if (e.key !== 'Enter' || e.repeat)
                    return;
                const labelNd = e.currentTarget;
                const inputNd = labelNd.previousElementSibling;
                inputNd?.click();
            });
            if (this.selectedItems.has(trade))
                input.checked = true;
            const handler = this.toggleSelection.bind(this);
            input.addEventListener('change', handler);
            div.append(input, label);
            elements.push(div);
        });
        // empty container and replace with new elements
        this.container.innerHTML = '';
        this.container.append(...elements, this.clearButton);
    }
    /**
     * Event handler called when a filter checkbox is selected or deselected.
     * Updates the internal selectedItems set and dispatches a change event.
     * Also manages the state of the clear button.
     *
     * @param {Event} evt - The change event from the checkbox input
     * @private
     */
    toggleSelection(evt) {
        const target = evt.currentTarget;
        const cot = target?.value;
        if (target?.checked)
            this.selectedItems.add(cot);
        else
            this.selectedItems.delete(cot);
        this.dispatchFilterChange();
        this.clearButton.disabled = this.selectedItems.size === 0;
    }
    /**
     * Dispatches a custom event to notify listeners of filter changes.
     * Emits an 'avails-facet-change' event with the current selection state.
     *
     * @fires AweClassOfTradeFilters#avails-facet-change
     * @private
     */
    dispatchFilterChange() {
        this.dispatchEvent(new CustomEvent('avails-facet-change', {
            bubbles: true,
            detail: {
                key: 'class-of-trade',
                values: Array.from(this.selectedItems),
            },
        }));
    }
    /**
     * Clears all selected filters and resets the component state.
     * Unchecks all checkbox inputs, clears the selectedItems set,
     * and dispatches a change event to notify listeners.
     */
    clearFilters() {
        this.selectedItems.clear();
        const inputs = this.container.querySelectorAll('.cot-filter input');
        for (const input of inputs) {
            input.checked = false;
        }
        this.dispatchFilterChange();
        this.clearButton.disabled = true;
    }
}
customElements.define('awe-cot-filters', AweClassOfTradeFilters);
// on filters change event, update availabilities
const filtersNd = document.getElementsByTagName('awe-cot-filters')[0];
if (filtersNd) {
    filtersNd.addEventListener('avails-facet-change', (e) => {
        const customEvent = e;
        player.getAvails(false, { classOfTrade: customEvent.detail.values });
    });
}
